// Ethiopian & Islamic Holidays with special themes
// Dates are approximate - Ethiopian calendar differs from Gregorian

interface Holiday {
  name: string;
  nameAmharic?: string;
  type: 'ethiopian' | 'islamic' | 'international';
  date: { month: number; day: number } | 'dynamic';
  theme: {
    accent: string;
    bg: string;
    emoji: string;
    greeting: string;
    greetingAmharic?: string;
  };
}

export const HOLIDAYS: Holiday[] = [
  // Ethiopian Orthodox Holidays
  {
    name: 'Enkutatash',
    nameAmharic: 'እንቁጣጣሽ',
    type: 'ethiopian',
    date: { month: 9, day: 11 }, // September 11 (or 12 in leap year)
    theme: {
      accent: '#fbbf24',
      bg: 'from-yellow-900/40 to-amber-900/20',
      emoji: '🌻',
      greeting: 'Happy Ethiopian New Year!',
      greetingAmharic: 'መልካም አዲስ ዓመት!',
    },
  },
  {
    name: 'Meskel',
    nameAmharic: 'መስቀል',
    type: 'ethiopian',
    date: { month: 9, day: 27 },
    theme: {
      accent: '#f97316',
      bg: 'from-orange-900/40 to-red-900/20',
      emoji: '🔥',
      greeting: 'Happy Meskel!',
      greetingAmharic: 'መልካም መስቀል!',
    },
  },
  {
    name: 'Genna',
    nameAmharic: 'ገና',
    type: 'ethiopian',
    date: { month: 1, day: 7 }, // January 7
    theme: {
      accent: '#22c55e',
      bg: 'from-green-900/40 to-emerald-900/20',
      emoji: '🎄',
      greeting: 'Merry Ethiopian Christmas!',
      greetingAmharic: 'መልካም ገና!',
    },
  },
  {
    name: 'Timkat',
    nameAmharic: 'ጥምቀት',
    type: 'ethiopian',
    date: { month: 1, day: 19 }, // January 19
    theme: {
      accent: '#3b82f6',
      bg: 'from-blue-900/40 to-cyan-900/20',
      emoji: '💧',
      greeting: 'Happy Timkat!',
      greetingAmharic: 'መልካም ጥምቀት!',
    },
  },
  {
    name: 'Fasika',
    nameAmharic: 'ፋሲካ',
    type: 'ethiopian',
    date: 'dynamic', // Varies each year
    theme: {
      accent: '#f472b6',
      bg: 'from-pink-900/40 to-rose-900/20',
      emoji: '🐣',
      greeting: 'Happy Ethiopian Easter!',
      greetingAmharic: 'መልካም ፋሲካ!',
    },
  },
  {
    name: 'Adwa Victory Day',
    nameAmharic: 'የዓድዋ ድል በዓል',
    type: 'ethiopian',
    date: { month: 3, day: 2 }, // March 2
    theme: {
      accent: '#16a34a',
      bg: 'from-green-900/40 to-yellow-900/20',
      emoji: '🇪🇹',
      greeting: 'Victory of Adwa! 🦁',
      greetingAmharic: 'የዓድዋ ድል!',
    },
  },
  {
    name: "Patriots' Victory Day",
    nameAmharic: 'የአርበኞች ድል ቀን',
    type: 'ethiopian',
    date: { month: 5, day: 5 }, // May 5
    theme: {
      accent: '#eab308',
      bg: 'from-yellow-900/40 to-green-900/20',
      emoji: '⚔️',
      greeting: "Happy Patriots' Day!",
      greetingAmharic: 'መልካም የአርበኞች ቀን!',
    },
  },
  {
    name: 'Derg Downfall Day',
    nameAmharic: 'ግንቦት 20',
    type: 'ethiopian',
    date: { month: 5, day: 28 }, // May 28
    theme: {
      accent: '#10b981',
      bg: 'from-emerald-900/40 to-teal-900/20',
      emoji: '✊',
      greeting: 'Freedom Day!',
      greetingAmharic: 'ግንቦት 20!',
    },
  },

  // Islamic Holidays (dates vary - using approximate)
  {
    name: 'Eid al-Fitr',
    type: 'islamic',
    date: 'dynamic',
    theme: {
      accent: '#22c55e',
      bg: 'from-green-900/40 to-emerald-900/20',
      emoji: '🌙',
      greeting: 'Eid Mubarak!',
    },
  },
  {
    name: 'Eid al-Adha',
    type: 'islamic',
    date: 'dynamic',
    theme: {
      accent: '#14b8a6',
      bg: 'from-teal-900/40 to-cyan-900/20',
      emoji: '🕌',
      greeting: 'Eid al-Adha Mubarak!',
    },
  },
  {
    name: 'Ramadan',
    type: 'islamic',
    date: 'dynamic', // Month-long
    theme: {
      accent: '#8b5cf6',
      bg: 'from-violet-900/40 to-purple-900/20',
      emoji: '☪️',
      greeting: 'Ramadan Mubarak!',
    },
  },
  {
    name: 'Mawlid',
    type: 'islamic',
    date: 'dynamic',
    theme: {
      accent: '#06b6d4',
      bg: 'from-cyan-900/40 to-sky-900/20',
      emoji: '🌟',
      greeting: 'Mawlid al-Nabi Mubarak!',
    },
  },

  // International
  {
    name: 'New Year',
    type: 'international',
    date: { month: 1, day: 1 },
    theme: {
      accent: '#fbbf24',
      bg: 'from-amber-900/40 to-yellow-900/20',
      emoji: '🎉',
      greeting: 'Happy New Year 2026!',
    },
  },
  {
    name: "Programmer's Day",
    type: 'international',
    date: { month: 9, day: 13 }, // 256th day of the year
    theme: {
      accent: '#22c55e',
      bg: 'from-green-900/40 to-slate-900/20',
      emoji: '💻',
      greeting: "Happy Programmer's Day!",
    },
  },
];

export function getCurrentHoliday(): Holiday | null {
  const now = new Date();
  const month = now.getMonth() + 1;
  const day = now.getDate();

  // Check for fixed-date holidays (within 3 days)
  for (const holiday of HOLIDAYS) {
    if (holiday.date !== 'dynamic') {
      const holidayMonth = holiday.date.month;
      const holidayDay = holiday.date.day;
      
      // Check if within 3 days before or after
      if (month === holidayMonth && Math.abs(day - holidayDay) <= 3) {
        return holiday;
      }
    }
  }

  return null;
}

export function getHolidayBanner(): { show: boolean; holiday: Holiday | null } {
  const holiday = getCurrentHoliday();
  return {
    show: holiday !== null,
    holiday,
  };
}


